import React, { useEffect, useState } from 'react'

/**
 *
 */
export type SelectOption = {
    value?: any
    text?: React.ReactNode
    title?: boolean
    selected?: boolean
    icon?: string
    disabled?: boolean
    renderComponent?: JSX.Element
}

/**
 *
 */
export const getOptions = (initialOptions: SelectOption[]) => initialOptions.map(initialOption => {
    const newOption: Record<string, any> = { ...initialOption }
    if (newOption.renderComponent) newOption.text = newOption.renderComponent
    return newOption
})

interface Params {
    initialOptions: SelectOption[]
    value: any
    selectMultiple: boolean
    required: boolean
    returnObject: boolean
    onChange?: (v: any) => any
}

/**
 *
 */
export const useSelectState = ({
    initialOptions,
    value,
    selectMultiple,
    required,
    returnObject,
    onChange
}: Params) => {
    const [isOpen, setIsOpen] = useState(false)
    const [selectedValue, setSelectedValue] = useState<any>(value || '')
    const [options, setOptions] = useState(getOptions(initialOptions))

    const isValueSelected = (optionValue) => {
        if (!selectMultiple) return optionValue === value
        return Array.isArray(value) && value.includes(optionValue)
    }

    useEffect(() => {
        const nextOptions = getOptions(initialOptions).map(opt => ({
            ...opt,
            selected: selectMultiple ? (opt.selected || isValueSelected(opt.value)) : opt.selected
        }))
        setOptions(nextOptions)

        if (selectMultiple) {
            const selected = nextOptions.filter(opt => opt.selected)
            setSelectedValue(selected)
        } else {
            const item = nextOptions.find(opt => opt.value === value)
            setSelectedValue(item?.value || '')
        }
    }, [initialOptions, value, selectMultiple])

    const onSelect = (val: any) => {
        const item = options.find(opt => opt.value === val)
        if (!item) return

        if (selectMultiple) {
            const updatedOptions = options.map(opt =>
                opt.value === item.value ? { ...opt, selected: !opt.selected } : opt
            )
            setOptions(updatedOptions)
            const selected = updatedOptions.filter(opt => opt.selected)
            setSelectedValue(selected)
            if (onChange) onChange(returnObject ? selected : selected.map(x => x.value))
            return
        }

        setSelectedValue(item.value === selectedValue && !required ? '' : item.value)
        setIsOpen(false)
        if (onChange) onChange(returnObject ? item : item.value)
    }

    const toggleDropdown = (disabled: boolean, onOpen?: () => void) => {
        if (disabled) return
        setIsOpen(!isOpen)
        if (!isOpen && onOpen) onOpen()
    }

    return {
        isOpen,
        setIsOpen,
        selectedValue,
        options,
        setOptions,
        onSelect,
        toggleDropdown
    }
}
